import React, { useState, useEffect, useRef } from 'react';
import { formatDateTime } from '../utils/mainfunction';
import { ChatListItem } from '../models/chat';
import { deleteChat, getChatHistory } from '../services/chatService';
import { getTexts } from '../utils/i18n';

const CHAT_TITLE_OVERRIDES_KEY = 'chatTitleOverrides';

const loadTitleOverrides = (): Record<string, string> => {
    try {
        const raw = localStorage.getItem(CHAT_TITLE_OVERRIDES_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch (error) {
        console.error('Chat title overrides parse error:', error);
        return {};
    }
};

const saveTitleOverrides = (overrides: Record<string, string>) => {
    try {
        localStorage.setItem(CHAT_TITLE_OVERRIDES_KEY, JSON.stringify(overrides));
    } catch (error) {
        console.error('Chat title overrides save error:', error);
    }
};

interface ChatSidebarProps {
    isOpen: boolean;
    onToggle: () => void;
    onSelectChat: (chatId: string) => void;
    onNewChat: () => void;
    currentChatId?: string;
    onDeleteChat?: (chatId: string) => void;
    setCurrentChatId: (chatId: string) => void;
    onChatHistoryLoaded?: (hasHistory: boolean) => void;
}

const ChatSidebar: React.FC<ChatSidebarProps> = ({
    isOpen,
    onToggle,
    onSelectChat,
    onNewChat,
    currentChatId,
    onDeleteChat,
    setCurrentChatId,
    onChatHistoryLoaded
}) => {
    const texts = getTexts();
    const [showOptions, setShowOptions] = useState<string | null>(null);
    const [chatHistory, setChatHistory] = useState<ChatListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [deletingChatId, setDeletingChatId] = useState<string | null>(null);
    const [titleOverrides, setTitleOverrides] = useState<Record<string, string>>(() => loadTitleOverrides());
    const [renamingChatId, setRenamingChatId] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const optionsMenuRef = useRef<HTMLDivElement | null>(null);
    const renameInputRef = useRef<HTMLInputElement | null>(null);

    // Chat geçmişini API'den çek
    const fetchChatHistory = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getChatHistory();
            if (response.responseStatus) {
                setChatHistory(response.chatList);
                const chatExists = !!currentChatId && response.chatList.some((chat) => chat.guid === currentChatId);
                const nextChatId = chatExists ? currentChatId : (response.chatList.length > 0 ? response.chatList[0].guid : '');
                setCurrentChatId(nextChatId);

                if (!nextChatId) {
                    sessionStorage.removeItem('chatGuid');
                }

                // Parent component'e chat geçmişi durumunu bildir
                if (onChatHistoryLoaded) {
                    onChatHistoryLoaded(response.chatList.length > 0);
                }
            } else {
                setError(response.responseMessage || texts.chatHistoryUnavailable);
                // Hata durumunda da bildir
                if (onChatHistoryLoaded) {
                    onChatHistoryLoaded(false);
                }
            }
        } catch (error) {
            console.error('Chat history error:', error);
            setError(texts.chatHistoryFetchError);
            // Hata durumunda da bildir
            if (onChatHistoryLoaded) {
                onChatHistoryLoaded(false);
            }
        } finally {
            setLoading(false);
        }
    };

    // Component mount olduğunda chat geçmişini çek
    useEffect(() => {
        fetchChatHistory();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (optionsMenuRef.current && !optionsMenuRef.current.contains(event.target as Node)) {
                setShowOptions(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const getCategoryIcon = (title: string) => {
        const lowerTitle = title.toLowerCase();

        if (lowerTitle.includes('randevu') || lowerTitle.includes('doktor') || lowerTitle.includes('appointment') || lowerTitle.includes('doctor')) {
            return '👨‍⚕️';
        } else if (lowerTitle.includes('diyet') || lowerTitle.includes('diet')) {
            return '🍎';
        } else if (lowerTitle.includes('sonuc') || lowerTitle.includes('degerlendir') || lowerTitle.includes('değerlendir') || lowerTitle.includes('result') || lowerTitle.includes('evaluate')) {
            return '📄';
        } else {
            return '💬';
        }
    };

    const getLastMessage = (chat: ChatListItem) => {
        // Get the last message from history
        const lastHistoryItem = chat.history[chat.history.length - 1];
        if (lastHistoryItem) {
            if (lastHistoryItem.isUser && lastHistoryItem.userMessage) {
                return lastHistoryItem.userMessage;
            } else if (!lastHistoryItem.isUser && lastHistoryItem.response) {
                // Remove HTML tags from response
                return lastHistoryItem.response.replace(/<[^>]*>/g, '');
            }
        }
        return texts.noMessageFound;
    };

    const truncateTitle = (title: string, maxLength: number = 25) => {
        if (title.length <= maxLength) return title;
        return title.substring(0, maxLength) + '...';
    };

    // Arama fonksiyonu - chat başlığı ve mesajları içinde arar
    const filterChats = (chats: ChatListItem[], query: string): ChatListItem[] => {
        if (!query.trim()) return chats;

        const lowerQuery = query.toLowerCase().trim();

        return chats.filter(chat => {
            // Başlıkta ara
            if (chat.title.toLowerCase().includes(lowerQuery)) return true;

            // Mesajlarda ara
            return chat.history.some(msg => {
                if (msg.userMessage && msg.userMessage.toLowerCase().includes(lowerQuery)) return true;
                if (msg.response) {
                    const plainText = msg.response.replace(/<[^>]*>/g, '').toLowerCase();
                    if (plainText.includes(lowerQuery)) return true;
                }
                return false;
            });
        });
    };

    // Yerel olarak yeniden adlandırılmış başlıkları uygula
    const displayedChatHistory = chatHistory.map((chat) =>
        titleOverrides[chat.guid] ? { ...chat, title: titleOverrides[chat.guid] } : chat
    );

    // Filtrelenmiş chat listesi
    const filteredChatHistory = filterChats(displayedChatHistory, searchQuery);

    const handleChatClick = (chatId: string) => {
        onSelectChat(chatId);
        sessionStorage.setItem('isChatStarted', 'true');
        setShowOptions(null);
    };

    const handleOptionsClick = (e: React.MouseEvent, chatId: string) => {
        e.stopPropagation();
        setShowOptions(showOptions === chatId ? null : chatId);
    };

    const handleDeleteChat = async (e: React.MouseEvent, chatId: string) => {
        e.stopPropagation();

        if (deletingChatId) return;

        setDeletingChatId(chatId);
        setError(null);

        try {
            const response = await deleteChat(chatId);

            if (response.responseStatus) {
                if (onDeleteChat) {
                    onDeleteChat(chatId);
                }
                setShowOptions(null);
                await fetchChatHistory();
            } else {
                setError(response.responseMessage || texts.deleteChatError);
            }
        } catch (error) {
            console.error('Delete chat error:', error);
            setError(texts.deleteChatError);
        } finally {
            setDeletingChatId(null);
        }
    };

    const handleRenameClick = (e: React.MouseEvent, chat: ChatListItem) => {
        e.stopPropagation();
        setShowOptions(null);
        setRenamingChatId(chat.guid);
        setRenameValue(titleOverrides[chat.guid] ?? chat.title);
    };

    const commitRename = (chatId: string) => {
        const trimmed = renameValue.trim();
        const originalChat = chatHistory.find((chat) => chat.guid === chatId);
        const currentTitle = originalChat ? (titleOverrides[chatId] ?? originalChat.title) : '';

        if (trimmed && trimmed !== currentTitle) {
            const nextOverrides = { ...titleOverrides, [chatId]: trimmed };
            setTitleOverrides(nextOverrides);
            saveTitleOverrides(nextOverrides);
        }

        setRenamingChatId(null);
    };

    const handleRenameCancel = () => {
        setRenamingChatId(null);
    };

    const handleNewChat = () => {
        onNewChat();
        sessionStorage.removeItem('isChatStarted');

        // Sidebar'ı kapat
        onToggle();

        // Yeni chat oluşturulduktan sonra listeyi yenile
        fetchChatHistory();
    };

    return (
        <>
            {/* Overlay sadece mobilde görünür */}
            {isOpen && (
                <div
                    className="sidebar-overlay"
                    onClick={onToggle}
                />
            )}

            <div
                className={`chat-sidebar ${isOpen ? 'open' : ''}`}
            >
                <div className="d-flex align-items-center justify-content-between p-3 ">
                    <div className="d-flex align-items-center gap-2">
                        <img
                            src="https://imagedelivery.net/mlgbP0181FH-5b9hEejSQw/741384eb-f43d-407b-7f26-d48bcb1f3100/public"
                            alt="HappAI"
                            className="rounded-circle"
                            height={32}
                        />
                        <span className="fs-5 chat-topbar-title">HappAI</span>
                    </div>

                    {/* Kapatma butonu sadece mobilde görünür */}
                    <button onClick={onToggle} className="chat-topbar-close sidebar-close-btn" aria-label={texts.close}>
                        <span>×</span>
                    </button>
                </div>

                <div className="p-3 border-bottom">
                    <button
                        onClick={handleNewChat}
                        className="new-chat-btn w-100"
                    >
                        {texts.newChat}
                    </button>
                </div>

                {/* Arama kutusu */}
                <div className="p-3 border-bottom">
                    <div className="position-relative">
                        <input
                            type="text"
                            className="form-control"
                            placeholder={texts.searchChats}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{
                                paddingLeft: '36px',
                                backgroundColor: '#f7f7fa',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px'
                            }}
                        />
                        <span
                            style={{
                                position: 'absolute',
                                left: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#9ca3af'
                            }}
                        >
                            🔍
                        </span>
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                style={{
                                    position: 'absolute',
                                    right: '8px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#9ca3af',
                                    fontSize: '16px',
                                    padding: '4px'
                                }}
                                aria-label={texts.clearSearch}
                            >
                                ×
                            </button>
                        )}
                    </div>
                </div>

                <div className="chat-sidebar-scroll pb-4 px-2">
                    {loading && (
                        <div className="d-flex justify-content-center align-items-center p-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">{texts.uploading}</span>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="alert alert-danger m-2" role="alert">
                            {error}
                            <button
                                className="btn btn-sm btn-outline-primary mt-2 w-100"
                                onClick={fetchChatHistory}
                            >
                                {texts.retry}
                            </button>
                        </div>
                    )}

                    {!loading && !error && (
                        <div className="d-flex flex-column gap-2">
                            {filteredChatHistory.length === 0 ? (
                                <div className="text-center text-muted p-4">
                                    {searchQuery ? (
                                        <p>{texts.noSearchResults(searchQuery)}</p>
                                    ) : (
                                        <>
                                            <p>{texts.noChatHistory}</p>
                                            <button
                                                className="btn btn-primary btn-sm"
                                                onClick={handleNewChat}
                                            >
                                                {texts.startFirstChat}
                                            </button>
                                        </>
                                    )}
                                </div>
                            ) : (
                                filteredChatHistory.map((chat) => (
                                    <div
                                        key={chat.guid}
                                        className={`chat-history-card position-relative border rounded p-3 ${currentChatId === chat.guid ? 'border-primary bg-light' : 'bg-white'}`}
                                        onClick={() => handleChatClick(chat.guid)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="d-flex justify-content-between">
                                            <div className="flex-grow-1">
                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                    <span className="fs-5">{getCategoryIcon(chat.title)}</span>
                                                    {renamingChatId === chat.guid ? (
                                                        <input
                                                            ref={renameInputRef}
                                                            type="text"
                                                            className="chat-title-rename-input"
                                                            value={renameValue}
                                                            autoFocus
                                                            onClick={(e) => e.stopPropagation()}
                                                            onChange={(e) => setRenameValue(e.target.value)}
                                                            onBlur={() => commitRename(chat.guid)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.preventDefault();
                                                                    commitRename(chat.guid);
                                                                } else if (e.key === 'Escape') {
                                                                    e.preventDefault();
                                                                    handleRenameCancel();
                                                                }
                                                            }}
                                                        />
                                                    ) : (
                                                        <h3 className="fs-6 fw-semibold text-dark text-truncate mb-0">
                                                            {truncateTitle(chat.title)}
                                                        </h3>
                                                    )}
                                                </div>
                                                <p className="text-muted small mb-1">
                                                    {getLastMessage(chat).length > 100
                                                        ? getLastMessage(chat).substring(0, 100) + '...'
                                                        : getLastMessage(chat)}
                                                </p>

                                                <p className="text-secondary small mb-0">
                                                    {formatDateTime(chat.lastMessageDate)}
                                                </p>
                                            </div>
                                            <div className="chat-history-options" ref={showOptions === chat.guid ? optionsMenuRef : null}>
                                                <button
                                                    onClick={(e) => handleOptionsClick(e, chat.guid)}
                                                    className="chat-options-trigger"
                                                    aria-label={texts.chatOptions}
                                                >
                                                    ⋯
                                                </button>

                                                {showOptions === chat.guid && (
                                                    <div className="chat-options-popup" role="menu" aria-label={texts.chatOptionsMenu}>
                                                        <button
                                                            onClick={(e) => handleDeleteChat(e, chat.guid)}
                                                            className="chat-options-delete"
                                                            disabled={deletingChatId === chat.guid}
                                                            aria-label={texts.deleteChatAria}
                                                        >
                                                            <span className="chat-options-delete-icon" aria-hidden="true">🗑</span>
                                                            <span>{deletingChatId === chat.guid ? texts.deleting : texts.deleteChat}</span>
                                                        </button>
                                                        <button
                                                            onClick={(e) => handleRenameClick(e, chat)}
                                                            className="chat-options-rename"
                                                            aria-label={texts.renameChatAria}
                                                        >
                                                            <span className="chat-options-rename-icon" aria-hidden="true">✏️</span>
                                                            <span>{texts.renameChat}</span>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default ChatSidebar;
